import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { BrandLogo } from '@/components/ui/brand-logo';
import { Loader2, Mail, Lock, ArrowRight, ArrowLeft, CheckCircle2, KeyRound, Ban, Sparkles, AlertTriangle, Ticket, Star, Users, LockKeyhole, ShieldCheck } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { InviteCodeStep } from '@/components/onboarding/InviteCodeStep';
import { isBetaMode, BETA_CONFIG as LAUNCH_BETA_CONFIG } from '@/lib/launch-mode';
import { WaitlistModal } from '@/components/landing/WaitlistModal';
import { getStoredReferralCode, clearStoredReferralCode } from '@/lib/referral';
import { reportError } from '@/lib/error-reporting';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const passwordSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Password must be at least 6 characters'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type AuthMode = 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'email-sent' | 'email-confirmed' | 'invite-code' | 'mfa-challenge';

// Local storage key for invite code (localStorage persists across tabs, unlike sessionStorage)
const INVITE_CODE_STORAGE_KEY = 'momentumeq_pending_invite_code';
const INVITE_CODE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// Normalize code: uppercase, remove spaces
const normalizeCode = (input: string): string => {
  return input.replace(/\s/g, '').toUpperCase();
};

// Store invite code in localStorage with expiry
const storeInviteCode = (code: string) => {
  try {
    const data = { code, timestamp: Date.now() };
    localStorage.setItem(INVITE_CODE_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

// Get invite code from localStorage (returns null if expired or not present)
const getStoredInviteCode = (): string | null => {
  try {
    const raw = localStorage.getItem(INVITE_CODE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Check expiry
    if (Date.now() - data.timestamp > INVITE_CODE_EXPIRY_MS) {
      localStorage.removeItem(INVITE_CODE_STORAGE_KEY);
      return null;
    }
    return data.code || null;
  } catch {
    return null;
  }
};

// Clear stored invite code from localStorage
const clearStoredInviteCode = () => {
  try {
    localStorage.removeItem(INVITE_CODE_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

// Coach invite token helpers (same pattern as team invite)
const COACH_INVITE_STORAGE_KEY = 'momentumeq_pending_coach_invite';
const COACH_INVITE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

const storeCoachInviteToken = (token: string) => {
  try {
    const data = { token, timestamp: Date.now() };
    localStorage.setItem(COACH_INVITE_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

const getStoredCoachInviteToken = (): string | null => {
  try {
    const raw = localStorage.getItem(COACH_INVITE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.timestamp > COACH_INVITE_EXPIRY_MS) {
      localStorage.removeItem(COACH_INVITE_STORAGE_KEY);
      return null;
    }
    return data.token || null;
  } catch {
    return null;
  }
};

const clearStoredCoachInviteToken = () => {
  try {
    localStorage.removeItem(COACH_INVITE_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

// Team invite token helpers (same pattern as invite code)
const TEAM_INVITE_STORAGE_KEY = 'momentumeq_pending_team_invite';
const TEAM_INVITE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days (matches invite expiry)

const storeTeamInviteToken = (token: string) => {
  try {
    const data = { token, timestamp: Date.now() };
    localStorage.setItem(TEAM_INVITE_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

const getStoredTeamInviteToken = (): string | null => {
  try {
    const raw = localStorage.getItem(TEAM_INVITE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.timestamp > TEAM_INVITE_EXPIRY_MS) {
      localStorage.removeItem(TEAM_INVITE_STORAGE_KEY);
      return null;
    }
    return data.token || null;
  } catch {
    return null;
  }
};

const clearStoredTeamInviteToken = () => {
  try {
    localStorage.removeItem(TEAM_INVITE_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

const Auth = () => {
  // If there's an invite code in URL, start in signup mode to encourage new users
  // If oauth=true flag is set, user is returning from Google OAuth with a code to redeem
  const [mode, setMode] = useState<AuthMode>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasOAuthFlag = urlParams.get('oauth') === 'true';
    const hasCode = !!urlParams.get('code');
    
    // If returning from OAuth with code, will transition to invite-code mode via useEffect
    // Start in login mode to avoid showing signup form briefly
    if (hasOAuthFlag && hasCode) {
      return 'login'; // useEffect will switch to invite-code when user is detected
    }
    return hasCode ? 'signup' : 'login';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; confirmPassword?: string }>({});
  const [searchParams] = useSearchParams();
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportEmail, setSupportEmail] = useState('');
  const [supportEmailError, setSupportEmailError] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  
  // Invite code validation state
  const [inviteCodeStatus, setInviteCodeStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [validatedInviteCode, setValidatedInviteCode] = useState<string | null>(null);
  const [manualInviteCode, setManualInviteCode] = useState(() => {
    const codeParam = searchParams.get('code');
    return codeParam ? normalizeCode(codeParam) : '';
  });
  const [inviteCodeError, setInviteCodeError] = useState<string | null>(null);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  // MFA challenge state
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [backupCodeVerifying, setBackupCodeVerifying] = useState(false);

  // Password reset timeout state
  const [resetTokenExpired, setResetTokenExpired] = useState(false);

  const { user, loading, signIn, signUp, signInWithGoogle, resetPassword, updatePassword, suspendedReason, isPasswordRecovery, clearPasswordRecovery } = useAuth();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Get invite code from URL params
  const inviteCodeFromUrl = searchParams.get('code');

  // Get team invite token from URL params (e.g. /auth?team_invite={token})
  const teamInviteTokenFromUrl = searchParams.get('team_invite');

  // Get coach invite token from URL params (e.g. /auth?coach_invite={token})
  const coachInviteTokenFromUrl = searchParams.get('coach_invite');

  // Validate invite code using secure RPC function
  const validateInviteCode = useCallback(async (code: string): Promise<{ valid: boolean; error?: string }> => {
    const normalized = normalizeCode(code);
    if (normalized.length < 8 || normalized.length > 20) {
      setInviteCodeStatus('invalid');
      setInviteCodeError("This code doesn't exist or has been deactivated.");
      return { valid: false, error: "This code doesn't exist or has been deactivated." };
    }

    setInviteCodeStatus('validating');
    setInviteCodeError(null);
    
    try {
      // Use secure RPC function instead of direct table query
      const { data, error } = await supabase.rpc('validate_invite_code', { p_code: normalized });

      if (error) {
        reportError(error, 'auth', { action: 'validate-invite-code' });
        setInviteCodeStatus('invalid');
        setInviteCodeError("This code doesn't exist or has been deactivated.");
        return { valid: false, error: "This code doesn't exist or has been deactivated." };
      }

      // Type assertion for RPC response
      const result = data as { valid: boolean; error?: string; tier?: string; duration_days?: number } | null;

      if (!result?.valid) {
        setInviteCodeStatus('invalid');
        const errorMessages: Record<string, string> = {
          'invalid_code': "This code doesn't exist or has been deactivated.",
          'code_expired': "This code has expired.",
          'code_exhausted': "This code has reached its limit.",
        };
        const errorMsg = errorMessages[result?.error as string] || "This code doesn't exist or has been deactivated.";
        setInviteCodeError(errorMsg);
        return { valid: false, error: errorMsg };
      }

      // Code is valid - store it for post-signup use
      setInviteCodeStatus('valid');
      setValidatedInviteCode(normalized);
      setInviteCodeError(null);
      
      // Store in localStorage for use after signup (persists across tabs)
      storeInviteCode(normalized);
      
      return { valid: true };
    } catch (err) {
      reportError(err, 'auth', { action: 'validate-invite-code' });
      setInviteCodeStatus('invalid');
      setInviteCodeError("Failed to validate code. Please try again.");
      return { valid: false, error: "Failed to validate code. Please try again." };
    }
  }, []);
  
  // Handle manual invite code submission
  const handleInviteCodeSubmit = async () => {
    if (!manualInviteCode.trim()) {
      setInviteCodeError("Please enter an invite code.");
      return;
    }
    
    const result = await validateInviteCode(manualInviteCode);
    if (result.valid) {
      // Navigate to signup with the validated code
      navigate(`/auth?code=${normalizeCode(manualInviteCode)}`);
    }
  };

  // Store invite code in localStorage immediately on mount (before validation)
  // This ensures the code is available for OAuth redirects and across browser tabs
  useEffect(() => {
    if (inviteCodeFromUrl) {
      const normalized = normalizeCode(inviteCodeFromUrl);
      if (normalized.length >= 3 && normalized.length <= 20) {
        storeInviteCode(normalized);
      }
    }
  }, [inviteCodeFromUrl]);

  // Store team invite token in localStorage on mount (for post-auth auto-accept)
  useEffect(() => {
    if (teamInviteTokenFromUrl) {
      storeTeamInviteToken(teamInviteTokenFromUrl);
    }
  }, [teamInviteTokenFromUrl]);

  // Store coach invite token in localStorage on mount (for post-auth auto-accept)
  useEffect(() => {
    if (coachInviteTokenFromUrl) {
      storeCoachInviteToken(coachInviteTokenFromUrl);
    }
  }, [coachInviteTokenFromUrl]);

  // Validate invite code from URL on mount
  useEffect(() => {
    if (inviteCodeFromUrl && inviteCodeStatus === 'idle') {
      validateInviteCode(inviteCodeFromUrl);
    }
  }, [inviteCodeFromUrl, inviteCodeStatus, validateInviteCode]);

  // Handle URL params for email confirmation and password reset
  useEffect(() => {
    const confirmed = searchParams.get('confirmed');
    const reset = searchParams.get('reset');

    if (confirmed === 'true') {
      setMode('email-confirmed');
    } else if (isPasswordRecovery) {
      // Only show reset form after PASSWORD_RECOVERY event fires (session is established)
      setMode('reset-password');
      setResetTokenExpired(false);
    }
    // Note: Don't set mode based on reset=true URL param alone - wait for isPasswordRecovery
    // to avoid "Auth session missing" errors when user submits before session is ready
  }, [searchParams, isPasswordRecovery]);

  // Timeout for password reset token processing
  // If reset=true is in URL but PASSWORD_RECOVERY event hasn't fired after 10s, show error
  useEffect(() => {
    const reset = searchParams.get('reset');
    if (reset === 'true' && !isPasswordRecovery) {
      const timeoutId = setTimeout(() => {
        if (!isPasswordRecovery) {
          setResetTokenExpired(true);
        }
      }, 10000); // 10 second timeout
      return () => clearTimeout(timeoutId);
    }
  }, [searchParams, isPasswordRecovery]);

  // Defensive check: If user isn't logged in but we're in invite-code mode, redirect to login
  // This prevents the confusing "You must be logged in" error in InviteCodeStep
  useEffect(() => {
    if (!loading && !user && mode === 'invite-code') {
      setMode('login');
    }
  }, [user, loading, mode]);

  // Auto-redeem invite code when user is logged in with a pending code
  useEffect(() => {
    const autoRedeemInviteCode = async () => {
      // Don't process if in invite-code mode (let user complete that first)
      if (!user || loading || mode === 'reset-password' || mode === 'invite-code' || mode === 'mfa-challenge' || isPasswordRecovery) {
        return;
      }

      // Check if MFA verification is pending before auto-redirecting
      const { data: assurance } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assurance?.currentLevel === 'aal1' && assurance?.nextLevel === 'aal2') {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totpFactor = factors?.totp?.find((f) => f.status === 'verified');
        if (totpFactor) {
          setMfaFactorId(totpFactor.id);
          setMode('mfa-challenge');
          return;
        }
      }

      // Check for pending invite code in localStorage or URL
      const pendingCode = getStoredInviteCode();
      const codeToRedeem = pendingCode || inviteCodeFromUrl;
      
      // Helper: process referral code if present (fire-and-forget)
      const processReferral = () => {
        const pendingReferral = getStoredReferralCode();
        if (pendingReferral) {
          supabase.rpc('process_referral', {
            p_referral_code: pendingReferral,
            p_referred_id: user.id,
          }).then(({ data: refData }) => {
            const refResult = typeof refData === 'string' ? JSON.parse(refData) : refData;
            if (refResult?.success || refResult?.error === 'already_referred') {
              clearStoredReferralCode();
            }
          }).catch(() => {});
        }
      };

      // If there's a pending invite code, try to auto-redeem it
      if (codeToRedeem) {
        const normalized = normalizeCode(codeToRedeem);

        try {
          // Try to redeem the code automatically
          const { data, error } = await supabase.rpc('redeem_invite_code', {
            p_code: normalized,
            p_user_id: user.id
          });

          if (!error) {
            const result = typeof data === 'string' ? JSON.parse(data) : data;

            if (result.success) {
              // Clear stored code on success
              clearStoredInviteCode();

              toast({
                title: '🎉 Welcome to the beta!',
                description: result.tier === 'free'
                  ? 'Your account is ready to use.'
                  : `You now have Pro access until ${result.expires_at ? new Date(result.expires_at).toLocaleDateString() : 'your subscription ends'}.`,
              });

              // Trigger beta welcome email if applicable
              if (result.tier !== 'free' && result.expires_at) {
                supabase.functions.invoke('send-beta-welcome', {
                  body: {
                    userId: user.id,
                    expirationDate: result.expires_at,
                  },
                }).catch(err => console.error('Failed to send welcome email:', err));
              }

              // Process pending referral code (fire-and-forget)
              processReferral();

              const lh = localStorage.getItem('momentum-last-home');
              navigate(lh === 'home' ? '/home' : '/dashboard');
              return;
            } else if (result.error === 'already_redeemed') {
              // Already redeemed this code, just continue
              clearStoredInviteCode();
              const lh = localStorage.getItem('momentum-last-home');
              navigate(lh === 'home' ? '/home' : '/dashboard');
              return;
            } else if (result.error === 'code_exhausted' || result.error === 'code_expired' || result.error === 'code_not_found') {
              // Code is no longer valid — clear it to prevent redirect loops
              clearStoredInviteCode();
              // User is already authenticated, let them enter a new code or proceed
              setMode('invite-code');
              setManualInviteCode('');
              setInviteCodeError(
                result.error === 'code_exhausted'
                  ? 'That invite code has reached its maximum number of uses. Please enter a different code.'
                  : result.error === 'code_expired'
                  ? 'That invite code has expired. Please enter a different code.'
                  : 'That invite code was not found. Please check and try again.'
              );
              return;
            }
          }
        } catch (err) {
          reportError(err, 'auth', { action: 'auto-redeem-invite' });
          // Clear stored code on network/unexpected errors to prevent loops
          clearStoredInviteCode();
        }

        // If auto-redeem failed, fall back to manual redemption step
        setMode('invite-code');
        return;
      }

      // No invite code — still process referral if present
      processReferral();

      // Check for pending team invite token
      const pendingTeamInvite = getStoredTeamInviteToken() || teamInviteTokenFromUrl;
      if (pendingTeamInvite) {
        try {
          const { data: teamResult, error: teamError } = await supabase.functions.invoke('accept-team-invite', {
            body: { token: pendingTeamInvite },
          });

          clearStoredTeamInviteToken();

          if (!teamError && teamResult?.success) {
            toast({
              title: teamResult.alreadyMember
                ? `You're already a member of ${teamResult.teamName}`
                : `You've joined ${teamResult.teamName}!`,
              description: teamResult.alreadyMember
                ? undefined
                : `Welcome to the team as a ${teamResult.role}.`,
            });
          } else if (teamResult?.error === 'team_full') {
            toast({
              title: 'Team is full',
              description: 'This team has no available seats. Contact the team admin.',
              variant: 'destructive',
            });
          } else if (teamResult?.error === 'invite_not_found_or_expired') {
            toast({
              title: 'Invite expired',
              description: 'This team invitation has expired or is no longer valid.',
              variant: 'destructive',
            });
          }
        } catch (err) {
          clearStoredTeamInviteToken();
          reportError(err, 'auth', { action: 'accept-team-invite' });
        }
      }

      // Check for pending coach invite token
      const pendingCoachInvite = getStoredCoachInviteToken() || coachInviteTokenFromUrl;
      if (pendingCoachInvite) {
        try {
          const { data: coachResult, error: coachError } = await supabase.functions.invoke('accept-coaching-engagement', {
            body: { token: pendingCoachInvite },
          });

          clearStoredCoachInviteToken();

          if (!coachError && coachResult?.success) {
            toast({
              title: coachResult.already_connected
                ? `You're already connected with ${coachResult.coach_name}`
                : `Connected with ${coachResult.coach_name}!`,
              description: coachResult.already_connected
                ? undefined
                : 'Your coach can now see your progress and provide guidance.',
            });
          } else if (coachResult?.error === 'invite_not_found_or_expired') {
            toast({
              title: 'Invite expired',
              description: 'This coaching invitation has expired or is no longer valid.',
              variant: 'destructive',
            });
          }
        } catch (err) {
          clearStoredCoachInviteToken();
          reportError(err, 'auth', { action: 'accept-coaching-invite' });
        }
      }

      const lh = localStorage.getItem('momentum-last-home');
      navigate(lh === 'home' ? '/home' : '/dashboard');
    };

    autoRedeemInviteCode();
  }, [user, loading, navigate, mode, inviteCodeFromUrl, teamInviteTokenFromUrl, coachInviteTokenFromUrl, toast]);

  const validateForm = () => {
    try {
      if (mode === 'forgot-password') {
        emailSchema.parse({ email });
      } else if (mode === 'reset-password') {
        passwordSchema.parse({ password, confirmPassword });
      } else if (mode === 'login') {
        loginSchema.parse({ email, password });
      } else {
        signupSchema.parse({ email, password });
      }
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { email?: string; password?: string; confirmPassword?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0] === 'email') fieldErrors.email = err.message;
          if (err.path[0] === 'password') fieldErrors.password = err.message;
          if (err.path[0] === 'confirmPassword') fieldErrors.confirmPassword = err.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes('suspended')) {
            toast({
              title: "Account Suspended",
              description: error.message,
              variant: "destructive",
            });
          } else if (error.message.includes('Invalid login credentials')) {
            toast({
              title: "Login failed",
              description: "Invalid email or password. Please try again.",
              variant: "destructive",
            });
          } else if (error.message.includes('Email not confirmed')) {
            toast({
              title: "Email not confirmed",
              description: "Please check your email and click the confirmation link.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            });
          }
        } else {
          // Check if MFA is required
          const { data: assurance } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (assurance?.currentLevel === 'aal1' && assurance?.nextLevel === 'aal2') {
            // User has MFA enrolled, need to verify
            const { data: factors } = await supabase.auth.mfa.listFactors();
            const totpFactor = factors?.totp?.find((f) => f.status === 'verified');
            if (totpFactor) {
              setMfaFactorId(totpFactor.id);
              setMode('mfa-challenge');
              setIsSubmitting(false);
              return;
            }
          }

          // If user logged in and has a pending invite code, redirect to redemption
          if (validatedInviteCode || inviteCodeFromUrl) {
            toast({
              title: "Welcome back!",
              description: "Now let's redeem your invite code.",
            });
            setMode('invite-code');
          } else {
            toast({
              title: "Welcome back!",
              description: "You've successfully logged in.",
            });
            // Navigate immediately — skip SmartRedirect entirely
            const lastHome = localStorage.getItem('momentum-last-home');
            navigate(lastHome === 'home' ? '/home' : '/dashboard');
          }
        }
      } else if (mode === 'signup') {
        const { error, needsEmailConfirmation } = await signUp(email, password);
        if (error) {
          if (error.message.includes('already registered')) {
            // If user has an invite code, tell them to login first then redeem
            if (validatedInviteCode || inviteCodeFromUrl) {
              toast({
                title: "Account already exists",
                description: "Please sign in to your existing account, then you can redeem your invite code.",
                variant: "destructive",
              });
              // Switch to login mode but keep the invite code
              setMode('login');
            } else {
              toast({
                title: "Account exists",
                description: "This email is already registered. Try logging in instead.",
                variant: "destructive",
              });
            }
          } else {
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            });
          }
        } else if (needsEmailConfirmation) {
          setMode('email-sent');
        } else {
          toast({
            title: "Account created!",
            description: "Welcome to MomentumEQ. Let's build your future!",
          });

          // Emit signup engagement event (fire-and-forget)
          import('@/lib/engagement-events').then(({ emitEngagementEvent }) => {
            emitEngagementEvent('signup_completed', { source: 'direct' });
          }).catch(() => {});

          // Skip SmartRedirect for new signups with no invite code
          if (!validatedInviteCode && !inviteCodeFromUrl) {
            navigate('/dashboard');
          }
        }
      } else if (mode === 'forgot-password') {
        const { error } = await resetPassword(email);
        if (error) {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        } else {
          setMode('email-sent');
          toast({
            title: "Check your email",
            description: "We've sent you a password reset link.",
          });
        }
      } else if (mode === 'reset-password') {
        const { error } = await updatePassword(password);
        if (error) {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        } else {
          clearPasswordRecovery();
          toast({
            title: "Password updated!",
            description: "Your password has been changed successfully.",
          });
          setPassword('');
          setConfirmPassword('');
          navigate('/');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaVerify = async () => {
    if (!mfaFactorId || mfaCode.length !== 6) return;
    setMfaVerifying(true);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId,
      });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challenge.id,
        code: mfaCode,
      });
      if (verifyError) throw verifyError;

      setMfaFactorId(null);
      setMode('login');
      toast({
        title: "Welcome back!",
        description: "You've successfully logged in.",
      });
      navigate('/');
    } catch (error: any) {
      reportError(error, 'auth', { action: 'mfa-verify' });
      toast({
        title: "Verification failed",
        description: "Invalid code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setMfaVerifying(false);
      setMfaCode('');
    }
  };

  const handleBackupCodeVerify = async () => {
    if (!backupCode.trim()) return;
    setBackupCodeVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-backup-code', {
        body: { code: backupCode.trim() },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "2FA disabled",
        description: "Your backup code was accepted. 2FA has been disabled. Please log in again.",
      });
      // Sign out and return to login — the user's MFA factor has been removed
      await supabase.auth.signOut();
      setMode('login');
      setMfaFactorId(null);
      setBackupCode('');
      setUseBackupCode(false);
    } catch (error: any) {
      reportError(error, 'auth', { action: 'backup-code-verify' });
      toast({
        title: "Invalid backup code",
        description: error.message || "The code is invalid or has already been used.",
        variant: "destructive",
      });
    } finally {
      setBackupCodeVerifying(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setErrors({});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleSupportSubmit = async () => {
    // Validate email format
    const emailValidation = z.string().trim().email('Please enter a valid email address').safeParse(supportEmail);
    if (!emailValidation.success) {
      setSupportEmailError(emailValidation.error.errors[0].message);
      return;
    }
    setSupportEmailError('');
    
    if (!supportMessage.trim()) return;
    
    setSupportSubmitting(true);
    try {
      const cleanEmail = supportEmail.trim();
      const cleanMessage = supportMessage.trim();

      // Use backend function to create ticket (bypasses RLS)
      const { data, error } = await supabase.functions.invoke('create-support-ticket', {
        body: {
          email: cleanEmail,
          subject: 'Account Suspension Appeal',
          message: cleanMessage,
          ticket_type: 'suspension_appeal',
          priority: 'high',
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: 'Message Sent',
        description: 'Your appeal has been submitted. We will review it shortly.',
      });
      setShowSupportForm(false);
      setSupportMessage('');
      setSupportEmail('');
    } catch (error) {
      reportError(error, 'auth', { action: 'support-ticket' });
      toast({
        title: 'Error',
        description: 'Failed to submit your message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSupportSubmitting(false);
    }
  };

  const renderContent = () => {
    // Suspended account message
    if (suspendedReason) {
      return (
        <Card className="card-elevated border-destructive/50">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <Ban className="w-6 h-6 text-destructive" />
            </div>
            <CardTitle className="font-display text-2xl text-destructive">Account Suspended</CardTitle>
            <CardDescription>
              Your account has been suspended
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <Ban className="h-4 w-4" />
              <AlertTitle>Reason</AlertTitle>
              <AlertDescription>{suspendedReason}</AlertDescription>
            </Alert>
            
            {!showSupportForm ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground text-center">
                  If you believe this is an error, you can appeal this decision.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowSupportForm(true)}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="support-email">Email</Label>
                  <Input
                    id="support-email"
                    type="email"
                    placeholder="you@example.com"
                    value={supportEmail}
                    onChange={(e) => {
                      setSupportEmail(e.target.value);
                      if (supportEmailError) setSupportEmailError('');
                    }}
                    className={supportEmailError ? 'border-destructive' : ''}
                    disabled={supportSubmitting}
                  />
                  {supportEmailError ? (
                    <p className="text-xs text-destructive">{supportEmailError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      We'll use this to reply to your ticket.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support-message">Your Message</Label>
                  <Textarea
                    id="support-message"
                    placeholder="Please explain why you believe your account was suspended in error..."
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    rows={4}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowSupportForm(false);
                      setSupportMessage('');
                      setSupportEmail('');
                      setSupportEmailError('');
                    }}
                    disabled={supportSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleSupportSubmit}
                    disabled={supportSubmitting || !supportMessage.trim() || !supportEmail.trim()}
                  >
                    {supportSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Appeal'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    // Email sent confirmation (for both signup and password reset)
    if (mode === 'email-sent') {
      return (
        <Card className="card-elevated border-border/50">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="font-display text-2xl">Check your email</CardTitle>
            <CardDescription>
              We've sent a confirmation link to <span className="font-medium text-foreground">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Click the link in your email to continue. If you don't see it, check your spam folder.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                resetForm();
                setMode('login');
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      );
    }

    // Email confirmed success - transition to invite code step
    if (mode === 'email-confirmed') {
      return (
        <Card className="card-elevated border-border/50">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            </div>
            <CardTitle className="font-display text-2xl">Email confirmed!</CardTitle>
            <CardDescription>
              Your email has been verified. You can now sign in to your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
              onClick={() => {
                resetForm();
                setMode('invite-code');
                navigate('/auth', { replace: true });
              }}
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                resetForm();
                setMode('login');
                navigate('/auth', { replace: true });
              }}
            >
              Go to sign in
            </Button>
          </CardContent>
        </Card>
      );
    }

    // Invite code redemption step (shown after email confirmation)
    if (mode === 'invite-code') {
      return (
        <InviteCodeStep
          initialCode={inviteCodeFromUrl || undefined}
          onComplete={() => {
            navigate('/');
          }}
          onSkip={() => {
            navigate('/');
          }}
        />
      );
    }

    // MFA challenge
    if (mode === 'mfa-challenge') {
      return (
        <Card className="card-elevated border-border/50">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="font-display text-2xl">Two-factor authentication</CardTitle>
            <CardDescription>
              {useBackupCode
                ? 'Enter one of your backup codes to disable 2FA and log in'
                : 'Enter the 6-digit code from your authenticator app'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!useBackupCode ? (
              <>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={mfaCode}
                    onChange={setMfaCode}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button
                  className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                  onClick={handleMfaVerify}
                  disabled={mfaVerifying || mfaCode.length !== 6}
                >
                  {mfaVerifying ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Verify
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <button
                  type="button"
                  onClick={() => setUseBackupCode(true)}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
                >
                  Use a backup code
                </button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="backupCode">Backup Code</Label>
                  <Input
                    id="backupCode"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXXXX"
                    className="font-mono text-center text-lg tracking-widest uppercase"
                    maxLength={8}
                    disabled={backupCodeVerifying}
                  />
                </div>
                <Button
                  className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                  onClick={handleBackupCodeVerify}
                  disabled={backupCodeVerifying || !backupCode.trim()}
                >
                  {backupCodeVerifying ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Use Backup Code
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setUseBackupCode(false);
                    setBackupCode('');
                  }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
                >
                  Use authenticator app instead
                </button>
              </>
            )}
            <Button
              variant="ghost"
              className="w-full"
              onClick={async () => {
                await supabase.auth.signOut();
                setMode('login');
                setMfaFactorId(null);
                setMfaCode('');
                setUseBackupCode(false);
                setBackupCode('');
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      );
    }

    // Forgot password form
    if (mode === 'forgot-password') {
      return (
        <Card className="card-elevated border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-2xl">Reset password</CardTitle>
            <CardDescription>
              Enter your email and we'll send you a reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Send reset link
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  resetForm();
                  setMode('login');
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to sign in
              </Button>
            </form>
          </CardContent>
        </Card>
      );
    }

    // Loading state while processing password recovery token
    // This shows when user lands on ?reset=true but session isn't established yet
    const resetParam = searchParams.get('reset');
    if (resetParam === 'true' && !isPasswordRecovery && mode !== 'reset-password') {
      // Check if token processing has timed out
      if (resetTokenExpired) {
        return (
          <Card className="card-elevated border-border/50">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle className="font-display text-2xl">Link expired or invalid</CardTitle>
              <CardDescription>
                This password reset link may have expired or is no longer valid.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Password reset links are valid for 24 hours. Please request a new one.
              </p>
              <Button
                className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                onClick={() => {
                  setResetTokenExpired(false);
                  setMode('forgot-password');
                  navigate('/auth', { replace: true });
                }}
              >
                Request new reset link
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setResetTokenExpired(false);
                  setMode('login');
                  navigate('/auth', { replace: true });
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to sign in
              </Button>
            </CardContent>
          </Card>
        );
      }

      // Still loading - show spinner
      return (
        <Card className="card-elevated border-border/50">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
            <CardTitle className="font-display text-2xl">Setting up...</CardTitle>
            <CardDescription>
              Please wait while we verify your reset link
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              This should only take a moment.
            </p>
          </CardContent>
        </Card>
      );
    }

    // Reset password form (after clicking email link)
    if (mode === 'reset-password') {
      return (
        <Card className="card-elevated border-border/50">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <KeyRound className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="font-display text-2xl">Set new password</CardTitle>
            <CardDescription>
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`pl-10 ${errors.password ? 'border-destructive' : ''}`}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`pl-10 ${errors.confirmPassword ? 'border-destructive' : ''}`}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-xs text-destructive">{errors.confirmPassword}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Update password
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </CardContent>
        </Card>
      );
    }

    // Check if we need to show beta-mode waitlist (signup without valid invite code)
    const betaMode = isBetaMode();
    
    // In beta mode, signup requires a valid invite code - show gate for:
    // 1. No code provided at all (idle)
    // 2. Invalid/expired code provided 
    // 3. Currently validating a code
    const needsInviteForSignup = betaMode && LAUNCH_BETA_CONFIG.requireInviteForSignup && mode === 'signup' && inviteCodeStatus !== 'valid';
    
    // Track validation state separately to avoid TS narrowing issues
    const isValidatingCode = inviteCodeStatus === 'validating';
    const hasInvalidCode = inviteCodeStatus === 'invalid';

    // Beta mode: Show waitlist/invite code required message for both no-code and invalid-code scenarios
    if (needsInviteForSignup) {
      return (
        <>
          <Card className="card-elevated border-border/50">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <LockKeyhole className="w-6 h-6 text-primary" />
              </div>
              <Badge className="mx-auto mb-4 bg-violet-500/20 text-violet-200 border border-violet-400/40 px-4 py-1.5">
                <Star className="w-3.5 h-3.5 mr-1.5 fill-violet-300" />
                Early Access Only
              </Badge>
              <CardTitle className="font-display text-2xl">MomentumEQ is in Private Beta</CardTitle>
              <CardDescription>
                To create an account, you need an invite code.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Show error if invalid code was provided */}
              {hasInvalidCode && inviteCodeFromUrl && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-center">
                  <p className="text-sm text-destructive font-medium">
                    {inviteCodeError || "This invite code is invalid or has expired."}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Please enter a valid code below or join the waitlist.
                  </p>
                </div>
              )}
              {/* Invite Code Input */}
              <div className="space-y-3">
                <Label htmlFor="invite-code">Invite Code *</Label>
                <Input
                  id="invite-code"
                  placeholder="BETA-XXXX-XXXX"
                  value={manualInviteCode}
                  className={`font-mono text-center text-lg tracking-widest uppercase ${inviteCodeError ? 'border-destructive' : ''}`}
                  maxLength={20}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setManualInviteCode(e.target.value.replace(/\s/g, '').toUpperCase());
                    setInviteCodeError(null);
                    setInviteCodeStatus('idle');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleInviteCodeSubmit();
                    }
                  }}
                  disabled={isValidatingCode}
                />
                {inviteCodeError && (
                  <p className="text-xs text-destructive text-center">{inviteCodeError}</p>
                )}
                <Button 
                  className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                  onClick={handleInviteCodeSubmit}
                  disabled={isValidatingCode || !manualInviteCode.trim()}
                >
                  {isValidatingCode ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              {/* Waitlist Option */}
              <div className="text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  Don't have a code?
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setShowWaitlistModal(true)}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Join the Waitlist
                </Button>
                <p className="text-xs text-muted-foreground">
                  Get notified when we open up.
                </p>
              </div>

              <div className="border-t border-border pt-4">
                {/* Already have account */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setErrors({});
                    }}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Already have an account?{' '}
                    <span className="text-primary font-medium">Sign In</span>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <WaitlistModal 
            open={showWaitlistModal}
            onOpenChange={setShowWaitlistModal}
          />
        </>
      );
    }

    // Login / Signup form
    return (
      <Card className="card-elevated border-border/50">
        {/* Beta badge for login */}
        {betaMode && mode === 'login' && (
          <div className="p-3 bg-violet-500/10 border-b border-violet-500/20 flex items-center justify-center gap-2 text-sm text-violet-300">
            <Star className="w-4 h-4 fill-violet-300" />
            Open Beta — Sign in to access your account
          </div>
        )}
        {/* Invite code banner for signup mode */}
        {mode === 'signup' && inviteCodeFromUrl && (
          <>
            {inviteCodeStatus === 'validating' && (
              <div className="p-3 bg-muted/50 border-b border-border flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Validating invite code...
              </div>
            )}
            {inviteCodeStatus === 'valid' && (
              <div className="p-3 bg-green-500/10 border-b border-green-500/20 flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-400">
                <Sparkles className="w-4 h-4" />
                Invite code applied! Complete signup to get Beta Pro access.
              </div>
            )}
            {inviteCodeStatus === 'invalid' && !betaMode && (
              <div className="p-3 bg-amber-500/10 border-b border-amber-500/20">
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>This invite code is invalid or has expired.</span>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-1">
                  You can still sign up for a free account.
                </p>
              </div>
            )}
          </>
        )}
        {/* Invite code banner for login mode when user has a pending code */}
        {mode === 'login' && (validatedInviteCode || inviteCodeFromUrl) && inviteCodeStatus === 'valid' && (
          <div className="p-3 bg-green-500/10 border-b border-green-500/20 flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-400">
            <Ticket className="w-4 h-4" />
            Sign in to redeem your invite code!
          </div>
        )}
        <CardHeader className="text-center">
          <CardTitle className="font-display text-2xl">
            {mode === 'login' ? 'Welcome back' : 'Get started'}
          </CardTitle>
          <CardDescription>
            {mode === 'login' 
              ? 'Sign in to continue your journey' 
              : inviteCodeStatus === 'valid'
                ? 'Create an account to unlock your Beta Pro access'
                : 'Create an account to track your goals'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                  disabled={isSubmitting}
                  autoFocus
                  aria-label="Email address"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-destructive" role="alert">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 ${errors.password ? 'border-destructive' : ''}`}
                  disabled={isSubmitting}
                  aria-label="Password"
                />
              </div>
              {errors.password && (
                <p className="text-xs text-destructive" role="alert">{errors.password}</p>
              )}
              {mode === 'login' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setErrors({});
                      setMode('forgot-password');
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {mode === 'login' ? 'Sign In' : 'Create Account'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={isSubmitting || isGoogleLoading}
              onClick={async () => {
                setIsGoogleLoading(true);
                const { error } = await signInWithGoogle();
                if (error) {
                  toast({
                    title: "Google sign-in failed",
                    description: error.message,
                    variant: "destructive",
                  });
                }
                setIsGoogleLoading(false);
              }}
            >
              {isGoogleLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              Continue with Google
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'login' ? 'signup' : 'login');
                setErrors({});
              }}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              disabled={isSubmitting}
            >
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <span className="text-primary font-medium">
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </span>
            </button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="text-center space-y-4">
          {/*
            Use the md logo (wider-safe viewBox) and let the container control the rendered size.
            This avoids any clipping in the auth modal without affecting the /welcome header logo.
          */}
          <div className="max-w-[320px] mx-auto">
            <BrandLogo size="md" className="w-full h-auto" />
          </div>
          <p className="text-muted-foreground mt-1">Build your future, daily</p>
        </div>

        {/* Auth Card */}
        {renderContent()}

        {/* 3E Framework teaser */}
        <p className="text-center text-sm text-muted-foreground">
          Track your growth through <span className="text-education">Education</span>,{' '}
          <span className="text-exposure">Exposure</span>, and{' '}
          <span className="text-experience">Experience</span>
        </p>
      </div>
    </div>
  );
};

export default Auth;
